/**
 * The silent `prompt=none` bounce is one shot per tab (AUTH-SYSTEM.md section
 * 5.3), normally spent on the first signed-out boot, while no central session
 * exists and the request can only fail. The one event that changes the world
 * is an interactive sign-in at the central login, so the login app appends
 * this fragment to the redirect it issues after creating a session without an
 * OIDC auth request, and consuming it here re-arms exactly one more attempt.
 *
 * The RETURN carries the signal rather than the gate's departure because the
 * gate is not the only way in: a login tab reached directly, reused from
 * earlier, or bookmarked signs the visitor in all the same, and a mark set
 * only by the gate's own handoff misses every one of those. A fragment never
 * reaches a server, survives the locale redirect, and is consumed by
 * replaceState, so the extra attempt stays a single shot rather than the poll
 * section 10 rules out: the bounce it triggers navigates without a fragment,
 * and a failed return therefore cannot re-arm itself. The literal is a wire
 * contract with `post-signin-redirect.ts` in the auth-login app.
 */
export const FRESH_CENTRAL_SESSION_FRAGMENT = 'bomdisoft_sso_fresh';

/**
 * One silent attempt per tab session. sessionStorage rather than a cookie: the
 * bounce marker must never outlive the browsing session, or a user who signs in
 * on the central login later would stay signed out here until it expired.
 */
export const SSO_ATTEMPTED_KEY = 'bomdisoft_sso_attempted';

export const consumeFreshCentralSession = (): boolean => {
  try {
    if (window.location.hash !== `#${FRESH_CENTRAL_SESSION_FRAGMENT}`) {
      return false;
    }
    window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}`);
    return true;
  } catch {
    return false;
  }
};
